import * as _ from 'underscore';
import { PagingDataMap } from './pagingDataMap';
import { RecursiveFilteringDataMap } from './recursiveFilteringDataMap';
import { SortingDataMap } from './sortingDataMap';
import { IRecursiveDataEntry } from '../../interfaces';
import {
	RecursiveDataSourceOptions,
	PagingOptions,
	SortOptions,
	FilterOptions
} from '../../interfaces/options';
import { DefaultPagingOptions } from '../../const/defaultOptions';
import { DataChangedEvent, RecursiveDataChangedEvent } from 'mappMvc/events';
import { Event } from '..';
import { IEvent } from '../../interfaces';
import { DataChangedEventType } from 'mappMvc/enums';

export class RecursiveDataSource {
	public isLoading: KnockoutObservable<boolean>;
	public view: KnockoutComputed<IRecursiveDataEntry[]>;
	public length: KnockoutComputed<number>;
	public parent: IRecursiveDataEntry;
	protected pagingDataMap: PagingDataMap<IRecursiveDataEntry>;
	protected recursiveFilteringDataMap: RecursiveFilteringDataMap;
	protected sortingDataMap: SortingDataMap<IRecursiveDataEntry>;
	private get: (dataSource: RecursiveDataSource, ...params: any[]) => Promise<any[]>;
	private data: KnockoutObservableArray<IRecursiveDataEntry>;
	private parentDataSource: RecursiveDataSource;
	private type: string;
	private options: RecursiveDataSourceOptions;
	private childProperties: string[];

	//Events
	private _onDataChanged: Event<RecursiveDataChangedEvent>;
	public get onDataChanged(): IEvent<RecursiveDataChangedEvent> {
		return this._onDataChanged;
	}

	constructor(options: RecursiveDataSourceOptions, type?: string, parent?: RecursiveDataSource) {
		this.options = $.extend(true, {}, options || {});
		var pagingOptions: PagingOptions = this.options.paging
			? $.extend(true, {}, DefaultPagingOptions, this.options.paging || {})
			: {};
		this.data = ko.observableArray<IRecursiveDataEntry>();
		this.parentDataSource = parent;
		this.type = type || this.options.root;
		this.isLoading = ko.observable(false);
		this.get = _.isFunction(options.get) ? options.get : this.defaultGet;
		this.childProperties = Object.keys(this.options.types[this.type]);
		this.recursiveFilteringDataMap = new RecursiveFilteringDataMap(this.data, options.filter);
		this.sortingDataMap = new SortingDataMap<IRecursiveDataEntry>(
			this.recursiveFilteringDataMap.map,
			options.sorting
		);
		this.pagingDataMap = new PagingDataMap(this.sortingDataMap.map, pagingOptions);
		this.view = this.pagingDataMap.map;
		if (options.data) {
			if (_.isArray(options.data) && options.data.length > 0) {
				this.setData(<any[]>options.data);
			} else if (ko.isObservable(options.data)) {
				this.setData((<KnockoutObservableArray<any>>options.data)());
			}
		}
		this.length = ko.pureComputed(() => this.data().length);

		//Events
		this._onDataChanged = new Event<RecursiveDataChangedEvent>();
		this.data.subscribe((newData: IRecursiveDataEntry[]) => {
			this._onDataChanged.notify(<RecursiveDataChangedEvent>{
				data: newData,
				dataSource: this,
				eventType: DataChangedEventType.CHANGED
			});
		});
	}

	protected getFlatChildrenData = (dataEntry: IRecursiveDataEntry): RecursiveDataSource[] => {
		var _flatChildrenData: RecursiveDataSource[] = [];
		this.childProperties.forEach((childProperty: string) => {
			var child = dataEntry.childEntries[childProperty];
			if (child) {
				_flatChildrenData = _flatChildrenData.concat(child);
			}
		});
		return _flatChildrenData;
	};

	private getChildType(property: string): string {
		return this.options.types[this.type][property];
	}

	protected setParent(parent: IRecursiveDataEntry) {
		this.parent = parent;
	}

	public setData(data: any[]) {
		var params = this.getParams();
		this.data.removeAll();
		var _dataSource: IRecursiveDataEntry[] = [];
		if (data && _.isArray(data) && data.length > 0) {
			data.forEach(_data => {
				var dataSourceEntry: IRecursiveDataEntry = {
					entry: _data,
					childEntries: {},
					parent: this.parent,
					type: this.type,
					params: params
				};
				this.childProperties.forEach((childProperty: string) => {
					var childDataSource = new RecursiveDataSource(
						this.options,
						this.getChildType(childProperty),
						this
					);
					childDataSource.setParent(dataSourceEntry);
					childDataSource.setData(<any[]>_data[childProperty]);
					dataSourceEntry.childEntries[childProperty] = childDataSource;
				});
				_dataSource.push(dataSourceEntry);
			});
		}
		this.data(_dataSource);
	}

	public reload(...params: any[]): Promise<RecursiveDataSource> {
		this.isLoading(true);
		return this.get
			.apply(this, ...params)
			.then(
				(data: any[]) => {
					this.setData(data);
				},
				() => {}
			)
			.then(() => this.isLoading(false));
	}

	public sort(sortSettings: SortOptions[]) {
		this.sortingDataMap.sort(sortSettings);
	}

	public filter(filterSettings: FilterOptions[]) {
		this.recursiveFilteringDataMap.filter(filterSettings);
	}

	public page(page: number) {
		this.pagingDataMap.page(page);
	}

	public previousPage() {
		this.pagingDataMap.previousPage();
	}

	public nextPage() {
		this.pagingDataMap.nextPage();
	}

	public firstPage() {
		this.pagingDataMap.firstPage();
	}

	public lastPage() {
		this.pagingDataMap.lastPage();
	}

	private defaultGet(dataSource: RecursiveDataSource): Promise<any[]> {
		var deferred = $.Deferred<any[]>();
		deferred.resolve(_.pluck(dataSource.data(), 'entry'));
		return deferred.promise();
	}

	private getParams(): any[] {
		return (this.options.params || []).concat(
			_.isFunction(this.options.getParams) ? this.options.getParams() : null
		);
	}
}
